// src/utils/roadGeometry.ts

import * as THREE from 'three';

export interface RibbonPoint {
  x: number;
  z: number;
}

/**
 * Calcula los puntos del polígono de la cinta de una carretera (en el plano XZ).
 * Recorre el centro y desplaza cada vértice medio-ancho hacia la izquierda y
 * hacia la derecha, devolviendo el contorno cerrado:
 *  [ borde izquierdo (de inicio a fin), borde derecho (de fin a inicio) ]
 * El polígono queda cerrado porque el primer vértice del borde derecho es
 * exactamente el último del izquierdo y viceversa.
 *
 * @param points - Puntos centrales de la carretera (x, z)
 * @param width  - Ancho de la carretera en metros
 * @returns Puntos del polígono cerrado
 */
export function buildRoadRibbon(
  points: RibbonPoint[],
  width: number
): RibbonPoint[] {
  if (!points || points.length < 2) return [];

  const halfWidth = (width || 8) / 2;

  const left: RibbonPoint[] = [];
  const right: RibbonPoint[] = [];

  for (let i = 0; i < points.length; i++) {
    const curr = points[i];
    const next = points[i + 1];
    const prev = points[i - 1];

    // Dirección suavizada para calcular el perpendicular
    let dir = new THREE.Vector3();
    if (prev && next) {
      const v1 = new THREE.Vector3(next.x - curr.x, 0, next.z - curr.z).normalize();
      const v2 = new THREE.Vector3(curr.x - prev.x, 0, curr.z - prev.z).normalize();
      dir = v1.add(v2).normalize();
    } else if (next) {
      dir = new THREE.Vector3(next.x - curr.x, 0, next.z - curr.z).normalize();
    } else if (prev) {
      dir = new THREE.Vector3(curr.x - prev.x, 0, curr.z - prev.z).normalize();
    } else {
      dir = new THREE.Vector3(1, 0, 0);
    }

    // Perpendicular en el plano XZ
    const perp = new THREE.Vector3(-dir.z, 0, dir.x).normalize();

    left.push({
      x: curr.x + perp.x * halfWidth,
      z: curr.z + perp.z * halfWidth,
    });
    right.push({
      x: curr.x - perp.x * halfWidth,
      z: curr.z - perp.z * halfWidth,
    });
  }

  // Contorno cerrado: por el borde izquierdo de ida y el derecho de vuelta
  return [...left, ...right.reverse()];
}
